import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { UploadSimple, X, ImageSquare, FilePdf } from "@phosphor-icons/react";
import { FileImage } from "@/components/ui/file-image";

/**
 * Reusable file-uploader.
 *
 * Props:
 *   value          — current file_id (or null)
 *   onChange(id)   — fires with new file_id after successful upload, or null on delete
 *   entityType     — optional metadata stored on the file record
 *   entityId       — optional metadata stored on the file record
 *   accept         — HTML input accept attribute (default: images + pdf)
 *   variant        — "avatar" | "attachment"  (default: "avatar")
 *   size           — pixel size for avatar preview (default 96)
 *
 * Limits enforced client-side (backend re-enforces): images ≤10MB, PDFs ≤25MB.
 */
export function FileUpload({
  value, onChange, entityType, entityId,
  accept = "image/jpeg,image/png,image/webp,application/pdf",
  variant = "avatar", size = 96,
}) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    const isImg = file.type.startsWith("image/");
    const limit = isImg ? 10 * 1024 * 1024 : 25 * 1024 * 1024;
    if (file.size > limit) {
      toast.error(`File too large. Max ${isImg ? "10 MB (images)" : "25 MB (PDFs)"}.`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = {};
      if (entityType) params.entity_type = entityType;
      if (entityId) params.entity_id = entityId;
      const res = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params,
      });
      onChange?.(res.data.id, res.data);
      toast.success("Uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };

  const remove = async () => {
    if (!value) return;
    setBusy(true);
    try {
      await api.delete(`/files/${value}`);
      onChange?.(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (variant === "avatar") {
    return (
      <div className="flex items-center gap-3">
        <div
          className="relative rounded-full bg-muted border border-border overflow-hidden shrink-0 flex items-center justify-center text-muted-foreground"
          style={{ width: size, height: size }}
        >
          {value ? (
            <FileImage
              fileId={value}
              alt=""
              className="w-full h-full object-cover"
              fallback={<ImageSquare size={size * 0.35} weight="duotone" />}
            />
          ) : (
            <ImageSquare size={size * 0.35} weight="duotone" />
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <input ref={ref} type="file" accept={accept} className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="file-input" />
          <Button size="sm" variant="outline" className="gap-1.5 h-8" disabled={busy} onClick={() => ref.current?.click()} data-testid="file-upload-btn">
            <UploadSimple size={12} weight="bold" /> {busy ? "Uploading…" : value ? "Change" : "Upload"}
          </Button>
          {value && (
            <Button size="sm" variant="ghost" className="gap-1.5 h-7 text-xs text-red-600 hover:text-red-700" disabled={busy} onClick={remove} data-testid="file-remove-btn">
              <X size={12} weight="bold" /> Remove
            </Button>
          )}
        </div>
      </div>
    );
  }

  // attachment variant — for PDFs / documents
  return (
    <div>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="file-input" />
      <Button size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={() => ref.current?.click()}>
        <FilePdf size={14} weight="duotone" /> {busy ? "Uploading…" : "Attach file"}
      </Button>
    </div>
  );
}
